import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  Banknote,
  Loader2,
  RefreshCw,
  Search,
  Ban,
  CheckCircle2,
  PlayCircle,
  AlertTriangle,
  Shield,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Calculator,
  Zap,
  UserCheck,
  FileText,
  Settings,
  Link2,
  ExternalLink,
  MousePointerClick,
  ArrowUpRight,
} from 'lucide-react';

const AdminReferralRewardsTable = () => {
  const { token } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [processingAction, setProcessingAction] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [payoutConfig, setPayoutConfig] = useState(null);
  const [referralCodes, setReferralCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesTotal, setCodesTotal] = useState(0);
  const [codesSearch, setCodesSearch] = useState('');
  const [codesPage, setCodesPage] = useState(0);
  const codesLimit = 20;
  const limit = 20;

  useEffect(() => {
    fetchRewards();
    fetchPayoutConfig();
    fetchReferralCodes();
  }, [page, statusFilter]);

  useEffect(() => {
    fetchReferralCodes();
  }, [codesPage]);

  const fetchPayoutConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayoutConfig(data);
      }
    } catch (err) {
      console.error('Failed to fetch payout config:', err);
    }
  };

  const fetchRewards = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('skip', page * limit);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(
        `${BACKEND_URL}/api/referrals/rewards?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setRewards(data.rewards || []);
        setStats(data.stats || {});
        setTotal(data.total || 0);
      } else {
        throw new Error('Failed to load rewards');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralCodes = async () => {
    setCodesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', codesLimit);
      params.append('skip', codesPage * codesLimit);
      if (codesSearch) params.append('search', codesSearch);

      const response = await fetch(
        `${BACKEND_URL}/api/referrals/admin/all?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setReferralCodes(data.referrals || []);
        setCodesTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch referral codes:', err);
    } finally {
      setCodesLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchRewards();
  };

  const handleRecompute = async (userId = null) => {
    setProcessingAction('recompute');
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/rewards/recompute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Recomputed ${data.processed} users`);
        fetchRewards();
      } else {
        throw new Error('Failed to recompute');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const handlePayout = async (userId = null) => {
    setProcessingAction('payout');
    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/execute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      });

      if (response.ok) {
        const data = await response.json();
        const modeText = data.live_mode ? '(LIVE)' : '(Mock)';
        toast.success(`${modeText} Processed ${data.processed} payouts: ${data.successful} successful, ${data.failed} failed`);
        fetchRewards();
        fetchPayoutConfig();
      } else {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to process payouts');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleAllowlistToggle = async (userId, enable) => {
    setProcessingAction(`allowlist-${userId}`);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/allowlist/${userId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: enable }),
      });

      if (response.ok) {
        toast.success(enable ? 'User added to payout allowlist' : 'User removed from payout allowlist');
        fetchRewards();
      } else {
        throw new Error('Failed to update allowlist');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReconcile = async () => {
    setProcessingAction('reconcile');
    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/reconcile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ days: 30 }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.report_id) {
          toast.success(`Reconciliation complete: ${data.summary?.matches || 0} matches, ${data.summary?.mismatches_count || 0} mismatches`);
        } else {
          toast.info(data.message || 'Reconciliation skipped');
        }
      } else {
        throw new Error('Reconciliation failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleBlock = async (userId) => {
    if (!blockReason || blockReason.length < 5) {
      toast.error('Please provide a reason (min 5 characters)');
      return;
    }

    setProcessingAction(`block-${userId}`);
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/rewards/${userId}/block`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: blockReason }),
      });

      if (response.ok) {
        toast.success('User blocked from payouts');
        setBlockReason('');
        fetchRewards();
      } else {
        throw new Error('Failed to block user');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleUnblock = async (userId) => {
    setProcessingAction(`unblock-${userId}`);
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/rewards/${userId}/unblock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('User unblocked');
        fetchRewards();
      } else {
        throw new Error('Failed to unblock user');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format((cents || 0) / 100);
  };

  const getStatusBadge = (status, blocked) => {
    if (blocked) {
      return <Badge variant="destructive"><Ban className="w-3 h-3 mr-1" /> Blocked</Badge>;
    }
    switch (status) {
      case 'eligible':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" /> Eligible</Badge>;
      case 'pending_payout':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'paid':
        return <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30"><Banknote className="w-3 h-3 mr-1" /> Paid</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-referral-rewards-table">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Banknote className="w-5 h-5 text-violet-400" />
            Referral Rewards Admin
            {payoutConfig?.live_mode && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 ml-2">
                <Zap className="w-3 h-3 mr-1" /> LIVE MODE
              </Badge>
            )}
            {payoutConfig && !payoutConfig.live_mode && (
              <Badge variant="outline" className="ml-2">
                Mock Mode
              </Badge>
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage referral reward payouts and user eligibility
          </p>
          {payoutConfig && (
            <p className="text-xs text-muted-foreground mt-1">
              Daily: {formatCurrency(payoutConfig.daily_total_cents)} / {formatCurrency(payoutConfig.daily_limit_cents)} used
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReconcile}
            disabled={processingAction === 'reconcile'}
            title="Reconcile with Stripe"
          >
            {processingAction === 'reconcile' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Reconcile
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRecompute()}
            disabled={processingAction === 'recompute'}
          >
            {processingAction === 'recompute' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4 mr-2" />
            )}
            Recompute All
          </Button>
          <Button
            size="sm"
            onClick={() => handlePayout()}
            disabled={processingAction === 'payout'}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {processingAction === 'payout' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-2" />
            )}
            Process Payouts
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!window.confirm('Reset monthly referral caps for all users? This will zero out monthly clicks, conversions, and revenue.')) return;
              setProcessingAction('reset-caps');
              try {
                const response = await fetch(`${BACKEND_URL}/api/referrals/admin/reset-monthly-caps`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                });
                const data = await response.json();
                if (response.ok) {
                  toast.success(data.message || 'Monthly caps reset successfully');
                  fetchRewards();
                } else {
                  toast.error(data.detail || 'Failed to reset monthly caps');
                }
              } catch (err) {
                toast.error('Failed to reset monthly caps');
              } finally {
                setProcessingAction(null);
              }
            }}
            disabled={processingAction === 'reset-caps'}
            data-testid="reset-monthly-caps-btn"
          >
            {processingAction === 'reset-caps' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Reset Monthly Caps
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const email = window.prompt('Enter the user email to seed referral test data for:', 'rc-creator@fundthehope.com');
              if (!email) return;
              setProcessingAction('seed-test');
              try {
                const response = await fetch(`${BACKEND_URL}/api/referrals/admin/seed-test-data`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ target_email: email })
                });
                const data = await response.json();
                if (response.ok) {
                  toast.success(`Seeded data for ${email}: ${data.stats?.total_clicks} clicks, ${data.stats?.total_conversions} conversions, $${data.stats?.total_revenue}`);
                  fetchRewards();
                } else {
                  toast.error(data.detail || 'Failed to seed test data');
                }
              } catch (err) {
                toast.error('Failed to seed test data');
              } finally {
                setProcessingAction(null);
              }
            }}
            disabled={processingAction === 'seed-test'}
            data-testid="seed-referral-test-data-btn"
          >
            {processingAction === 'seed-test' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-2" />
            )}
            Seed QA Test Data
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-lg p-4 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Total Users</p>
            <p className="text-2xl font-bold text-foreground">{stats.total_users || 0}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Eligible</p>
            <p className="text-2xl font-bold text-emerald-400">
              {stats.by_status?.eligible?.count || 0}
            </p>
          </div>
          <div className="bg-card rounded-lg p-4 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Blocked</p>
            <p className="text-2xl font-bold text-red-400">{stats.blocked_users || 0}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Total Paid</p>
            <p className="text-2xl font-bold text-violet-400">
              {formatCurrency(stats.by_status?.paid?.paid || 0)}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 flex gap-2">
          <Input
            placeholder="Search by user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="max-w-sm"
          />
          <Button variant="outline" onClick={handleSearch}>
            <Search className="w-4 h-4" />
          </Button>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-3 py-2 bg-secondary border border-border rounded-md text-sm"
        >
          <option value="">All Statuses</option>
          <option value="eligible">Eligible</option>
          <option value="ineligible">Ineligible</option>
          <option value="pending_payout">Pending Payout</option>
          <option value="paid">Paid</option>
          <option value="blocked">Blocked</option>
          <option value="failed">Failed</option>
        </select>
        <Button variant="outline" onClick={fetchRewards}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Eligible</TableHead>
              <TableHead className="text-right">Pending</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Connect</TableHead>
              <TableHead>Allowlist</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : rewards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No reward records found
                </TableCell>
              </TableRow>
            ) : (
              rewards.map((reward) => (
                <TableRow key={reward.referrer_user_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-foreground">{reward.user_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{reward.user_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(reward.status, reward.blocked)}</TableCell>
                  <TableCell className="text-right font-medium text-emerald-400">
                    {formatCurrency(reward.eligible_amount_cents)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatCurrency(reward.pending_amount_cents)}
                  </TableCell>
                  <TableCell className="text-right text-violet-400">
                    {formatCurrency(reward.paid_amount_cents)}
                  </TableCell>
                  <TableCell>
                    {reward.stripe_connect_status === 'active' ? (
                      <Badge variant="outline" className="text-emerald-400 border-emerald-500/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-400 border-amber-500/30">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Not Ready
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={reward.payout_allowlisted ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleAllowlistToggle(reward.referrer_user_id, !reward.payout_allowlisted)}
                      disabled={processingAction?.startsWith('allowlist')}
                      className={reward.payout_allowlisted 
                        ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30' 
                        : 'text-muted-foreground hover:text-foreground'}
                      title={reward.payout_allowlisted ? 'Remove from allowlist' : 'Add to allowlist'}
                      data-testid={`allowlist-toggle-${reward.referrer_user_id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      <span className="text-xs">{reward.payout_allowlisted ? 'Yes' : 'No'}</span>
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePayout(reward.referrer_user_id)}
                        disabled={processingAction === 'payout' || reward.blocked}
                        title="Trigger Payout"
                      >
                        <PlayCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRecompute(reward.referrer_user_id)}
                        disabled={processingAction === 'recompute'}
                        title="Recompute"
                      >
                        <Calculator className="w-4 h-4" />
                      </Button>
                      {reward.blocked ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnblock(reward.referrer_user_id)}
                          disabled={processingAction?.startsWith('unblock')}
                          className="text-emerald-400"
                          title="Unblock"
                        >
                          <Shield className="w-4 h-4" />
                        </Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400"
                              title="Block"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Block User from Payouts</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will prevent {reward.user_name || 'this user'} from receiving any referral payouts. Please provide a reason.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <Input
                              placeholder="Reason for blocking..."
                              value={blockReason}
                              onChange={(e) => setBlockReason(e.target.value)}
                            />
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleBlock(reward.referrer_user_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Block User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Referral Codes Section */}
      <div className="mt-8 space-y-4" data-testid="admin-referral-codes-section">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Link2 className="w-5 h-5 text-blue-400" />
            All Referral Codes
            {codesTotal > 0 && (
              <Badge variant="outline" className="ml-1 text-blue-400 border-blue-500/30">{codesTotal}</Badge>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search by code or user..."
              value={codesSearch}
              onChange={(e) => setCodesSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setCodesPage(0); fetchReferralCodes(); } }}
              className="max-w-xs text-sm"
              data-testid="admin-referral-codes-search"
            />
            <Button variant="outline" size="sm" onClick={() => { setCodesPage(0); fetchReferralCodes(); }}>
              <Search className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={fetchReferralCodes}>
              <RefreshCw className={`w-4 h-4 ${codesLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Referral Link</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Linked To</TableHead>
                <TableHead className="text-center">Clicks</TableHead>
                <TableHead className="text-center">Conversions</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead>Reward</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codesLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : referralCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    No referral codes found
                  </TableCell>
                </TableRow>
              ) : (
                referralCodes.map((ref) => {
                  const baseUrl = window.location.origin;
                  const refLink = ref.referral_code ? `${baseUrl}/?ref=${ref.referral_code}` : '';
                  const linkedName = ref.campaign_title || ref.team_page_title || (ref.campaign_id ? ref.campaign_id : null) || (ref.team_page_id ? ref.team_page_id : null) || '—';

                  return (
                    <TableRow key={ref.id}>
                      <TableCell>
                        <code className="text-xs bg-secondary px-2 py-1 rounded font-mono font-medium text-foreground">
                          {ref.referral_code || '—'}
                        </code>
                      </TableCell>
                      <TableCell className="max-w-[160px]">
                        {refLink ? (
                          <span className="text-xs text-muted-foreground truncate block" title={refLink}>
                            {refLink.slice(0, 40)}...
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-foreground">{ref.referrer_name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{ref.referrer_email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          ref.referral_type === 'campaign' ? 'text-emerald-400 border-emerald-500/30' :
                          ref.referral_type === 'team' ? 'text-violet-400 border-violet-500/30' :
                          'text-blue-400 border-blue-500/30'
                        }>
                          {ref.referral_type || 'general'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                        {linkedName}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {ref.clicks_count ?? ref.clicks ?? 0}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {ref.conversions_count ?? ref.conversions ?? 0}
                      </TableCell>
                      <TableCell className="text-right font-medium text-emerald-400">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(ref.total_revenue_generated || ref.reward_amount || 0)}
                      </TableCell>
                      <TableCell>
                        {ref.reward_issued ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Issued</Badge>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-muted-foreground">Pending</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                              data-testid={`mark-issued-btn-${ref.referral_code || ref.id}`}
                              onClick={async () => {
                                const codeId = ref.referral_code || ref.id;
                                try {
                                  const response = await fetch(`${BACKEND_URL}/api/referrals/admin/${codeId}/mark-reward-issued`, {
                                    method: 'PATCH',
                                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                                  });
                                  if (response.ok) {
                                    toast.success('Reward marked as issued');
                                    fetchCodes();
                                  } else {
                                    const data = await response.json();
                                    toast.error(data.detail || 'Failed to mark reward');
                                  }
                                } catch (err) {
                                  toast.error('Failed to mark reward as issued');
                                }
                              }}
                            >
                              Mark Issued
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Codes Pagination */}
        {Math.ceil(codesTotal / codesLimit) > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {codesPage * codesLimit + 1}-{Math.min((codesPage + 1) * codesLimit, codesTotal)} of {codesTotal}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setCodesPage(codesPage - 1); }} disabled={codesPage === 0}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {codesPage + 1} of {Math.ceil(codesTotal / codesLimit)}
              </span>
              <Button variant="outline" size="sm" onClick={() => { setCodesPage(codesPage + 1); }} disabled={codesPage >= Math.ceil(codesTotal / codesLimit) - 1}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReferralRewardsTable;
